import {Fragment, useCallback, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useDispatch} from 'react-redux';
import moment from 'moment';
import clsx from 'clsx';
import {Paper, Stack} from '@mui/material';
import Typography from '@mui/material/Typography';
import {cleanDate, getSessionId} from '../../../../../../utils/edReviewUtils';
import {
  pushToTemporaryEventsList,
  removeFromTemporaryEventsList,
  setModalHelperState,
} from '../../../../../../../../../../store/reducers/reviews';
import {DEFAULT_LEFT, SLOT_HEIGHT} from '../../edReviewCalendarDaily';
import {updateMousePosition} from '../../../../utils/sessionUtilities';
import LockedSlotTile from './lockedSlotTile';
import AvailableSlotTile from './availableSlotTile';
import {useDailyCalendarTileStyles} from './dailyCalendarTileStyles';
import PatientInfoBox from './patientInfo';

const DailyCalendarTile = ({
  id,
  event,
  options,
  session,
  onDelete,
  selected,
  columnIndex,
  tileSelected,
  patientAssign,
  onSelectTime,
  onButtonClick,
  locked,
  availableSlot,
  temporaryLock,
  changeLockedReason,
  deleteTemporaryLockedSession,
}) => {
  const dispatch = useDispatch();
  const classes = useDailyCalendarTileStyles({sessionName: event?.sessionName || ""});

  const [cursorLocation, setCursorLocation] = useState({x: 0, y: 0});
  const [showTimeAboveCursor, setShowTimeAboveCursor] = useState(false);

  const sessionPaperRef = useRef();

  const onClickAvailableSlot = (ev) => {
    onButtonClick(ev);
    dispatch(pushToTemporaryEventsList(ev?.sessionId, ev, columnIndex));

    if (patientAssign) {
      const passToDischargeFormObj = {
        session: getSessionId(ev),
        hour: moment(ev?.startDate).format("h:mm a"),
      };
      onSelectTime(event, false, ev, columnIndex);
      dispatch(setModalHelperState(passToDischargeFormObj))
    }
  }

  const onPaperClick = useCallback((ev) => {
    ev.stopPropagation();

    return availableSlot && onSelectTime ?
      onSelectTime(ev, false, event, columnIndex) :
      null
  }, [availableSlot, onSelectTime, event, columnIndex]);

  const onClickRemoveSlot = (ev) => {
    if (onDelete) {
      onDelete(ev, true);
    } else {
      const idToPass = ev?.parentSession && ev?.parentSession !== 'outsideSession' ? ev.parentSession : ev.sessionId;
      dispatch(removeFromTemporaryEventsList(idToPass, ev, patientAssign));
    }
  }

  const onMouseLeave = (e) => {
    if (availableSlot) {
      setShowTimeAboveCursor(false);
    }
  }

  const onMouseOver = (e) => {
    if (!availableSlot) {
      setShowTimeAboveCursor(false);
    } else if (availableSlot && !showTimeAboveCursor) {
      setShowTimeAboveCursor(false);
    }
  }

  const onMouseEnter = (e) => {
    if (availableSlot) {
      setShowTimeAboveCursor(true);
    }
  }

  const calculateTimeForTop = (top) => {
    const minutesFromStartOfDay = (top / SLOT_HEIGHT) * 30;
    const startOfDay = cleanDate(moment(event?.startDate).startOf("day"));
    return startOfDay.add(minutesFromStartOfDay, "minutes");
  };

  const calculateTopForCurrentTime = (time) => {
    const startOfDay = moment().startOf("day");
    const minutesFromStartOfDay = moment(time).diff(startOfDay, "minutes");
    const top = (minutesFromStartOfDay / 30) * SLOT_HEIGHT - (time ? 15 : 0);
    return `${top}px`;
  };

  const calculatedHour = useMemo(() => calculateTimeForTop(cursorLocation.y)
    , [cursorLocation?.y]
  );

  const handleWatchAndUpdateMousePosition = (e) => {
    if (showTimeAboveCursor && sessionPaperRef.current) {
      updateMousePosition(e, sessionPaperRef, setCursorLocation)
    }
  }

  const returnElementType = () => {
    const eventId = getSessionId(event);

    if (session) {
      return (
        <Paper
          key={eventId}
          ref={sessionPaperRef}
          className={clsx(
            classes.session,
            locked && "locked",
            temporaryLock && "temporary",
            selected && "selected",
            availableSlot && 'available',
            classes.animation,
          )}
          style={{
            top: options.top,
            left: options.left,
            width: options.width,
            height: options.height,
            zIndex: availableSlot ? 101 : locked ? 103 : 1,
            cursor: options.cursor ? options.cursor : availableSlot ? 'pointer' : 'default,'
          }}
          onMouseOver={onMouseOver}
          onMouseLeave={onMouseLeave}
          onMouseEnter={onMouseEnter}
          onMouseMove={handleWatchAndUpdateMousePosition}
          onClick={(...args) => onClickRemoveSlot ? null : onPaperClick(...args)}
        >
          {
            availableSlot ?
              <AvailableSlotTile
                event={event}
                locked={locked}
                tileSelected={tileSelected}
                patientAssign={patientAssign}
                onClickRemoveSlot={onClickRemoveSlot}
                onClickAvailableSlot={onClickAvailableSlot}
              /> :
              (locked || temporaryLock) ?
                <LockedSlotTile
                  event={event}
                  temporaryLock={temporaryLock}
                  changeLockedReason={changeLockedReason}
                  deleteTemporaryLockedSession={deleteTemporaryLockedSession}
                /> :
                <div onClick={onSelectTime} />
          }
        </Paper>
      );
    } else {
      return (
        <Paper
          id={id}
          key={eventId}
          className={classes.review}
          // onClick={(e) => !availableSlot ? onSelectTime : null}
          style={{
            zIndex: 100,
            display: 'flex',
            top: options.top,
            left: options.left,
            width: options.width,
            height: options.height,
          }}
        >
          <PatientInfoBox  ptInWard={event} calendarView />
          {/*<PatientToBeSeenCtx ptInWard={event} calendarView />*/}
        </Paper>
      );
    }
  };

  return (
    <>
      {(cursorLocation.x !== 0 &&
        showTimeAboveCursor &&
        moment.isMoment(calculatedHour)
      ) && (
        <Stack
          className={classes.timeIndicator}
          style={{
            left: cursorLocation.x - (1.2 * DEFAULT_LEFT),
            top: calculateTopForCurrentTime(calculateTimeForTop(cursorLocation.y - 35)) ,
          }}
        >
          <Typography variant='body1' fontWeight={500}>
            {cursorLocation?.y !== 0 && calculatedHour.format("HH:mm")}
          </Typography>
        </Stack>
      )}

      {returnElementType()}
    </>
  );
};

DailyCalendarTile.propTypes = {
  event: PropTypes.object.isRequired,
  options: PropTypes.object.isRequired,
  session: PropTypes.bool,
  selected: PropTypes.bool,
  locked: PropTypes.bool,
  availableSlot: PropTypes.bool,
  temporaryLock: PropTypes.bool,
  onSelectTime: PropTypes.func,
  changeLockedReason: PropTypes.func,
  deleteTemporaryLockedSession: PropTypes.func,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

DailyCalendarTile.defaultProps = {
  locked: false,
  session: false,
  selected: false,
  availableSlot: false,
  temporaryLock: false,
};

export default DailyCalendarTile;
